import '../App.css';
import React, { useEffect } from 'react';
import { auth } from '../firebase';
import { useState } from 'react';
import { signInAnonymously } from "firebase/auth";
import { logDemographics, logNewUser } from '../logging.js';
import { TextField, Flex, Button, Form, Provider, defaultTheme, Text, Heading, Radio, RadioGroup, ContextualHelp, Content, CheckboxGroup, Checkbox, TextArea } from '@adobe/react-spectrum';

function DemographicsForm({workerId, experiment, participantId, callbacks}) {
    // DEBUG include topics of interest
    const useTopics = false;

    // Basic Information
    const [age, setAge] = useState('');
    const [gender, setGender] = useState('');
    const [languageProficiency, setLanguageProficiency] = useState('');
    const [education, setEducation] = useState('');

    // Experience
    const [readerFrequency, setReaderFrequency] = useState('');
    const [highlightFrequency, setHighlightFrequency] = useState('');
    const [highlightBehaviours, setHighlightBehaviours] = useState([]);
    const [additionalComments, setAdditionalComments] = useState('');
    
    // Topics
    const [naturalSciencesInterest, setNaturalSciencesInterest] = useState('');
    const [engineeringInterest, setEngineeringInterest] = useState('');
    const [newsPoliticsInterest, setNewsPoliticsInterest] = useState('');
    const [literatureInterest, setLiteratureInterest] = useState('');
    const [historyInterest, setHistoryInterest] = useState('');

    // Validation
    const [formValidated, setFormValidated] = useState(false);

    useEffect(() => {
        validateForm();
    }, [age, gender, languageProficiency, education, readerFrequency, highlightFrequency, highlightBehaviours, additionalComments, naturalSciencesInterest, engineeringInterest, newsPoliticsInterest, literatureInterest, historyInterest]);

    function validateForm() {
        if (useTopics) {
            if (age !== '' && gender !== '' && languageProficiency !== '' && education !== '' && readerFrequency !== '' && naturalSciencesInterest !== '' && engineeringInterest !== '' && newsPoliticsInterest !== '' && literatureInterest !== '' && historyInterest !== '') {
                
                // all of the mandatory fields are filled out
                if (readerFrequency === 'never'){
                    setFormValidated(true);
                }
                
                else {
                    if (highlightFrequency === 'never'){
                        setFormValidated(true);
                    }
                    
                    else {
                        if (highlightBehaviours.length > 0) {
                            // filled everything out
                            setFormValidated(true);                 
                        }
                        else {
                            // highlight behaviours not filled out
                            setFormValidated(false);
                        }
                    }
                }
            }
            else {
                // some of the mandatory fields are not filled out
                setFormValidated(false);
            }
        }

        else {
            if (age !== '' && gender != '' && languageProficiency !== '' && education !== '' && readerFrequency !== '') {
                // all of the mandatory fields are filled out
                if (readerFrequency === 'never'){
                    setFormValidated(true);
                }
                
                else {
                    if (highlightFrequency === 'never'){
                        setFormValidated(true);
                    }
                    
                    else {
                        if (highlightBehaviours.length > 0) {
                            // filled everything out
                            setFormValidated(true);                 
                        }
                        else {
                            // highlight behaviours not filled out
                            setFormValidated(false);
                        }
                    }
                }
            }
            else {
                // some of the mandatory fields are not filled out
                setFormValidated(false);
            }
        }
    }

    function submitForm(e) {
        e.preventDefault();
        var demographics;
        if (useTopics) {
            demographics = {
                participantId: participantId,
                age: age,
                gender: gender,
                languageProficiency: languageProficiency,
                education: education,
                readerFrequency: readerFrequency,
                highlightFrequency: highlightFrequency,
                highlightBehaviours: highlightBehaviours,
                additionalComments: additionalComments,
                naturalSciencesInterest: naturalSciencesInterest,
                engineeringInterest: engineeringInterest,
                newsPoliticsInterest: newsPoliticsInterest,
                literatureInterest: literatureInterest,
                historyInterest: historyInterest
            }
    } else {
        demographics = {
            workerId: workerId,
            participantId: participantId,
            experiment: experiment,
            age: age,
            gender: gender,
            languageProficiency: languageProficiency,
            education: education,
            readerFrequency: readerFrequency,
            highlightFrequency: highlightFrequency,
            highlightBehaviours: highlightBehaviours,
            additionalComments: additionalComments
        }
    }

        logDemographics(demographics, callbacks);
    }

    return (
        <>    
            <Provider theme={defaultTheme} colorScheme='light'>
                <Flex direction="column" justifyContent={'center'} alignItems={"center"}>             
                    <div id="textToRead" className='documentReader'>
                        <Heading level={1} width="100%" marginBottom="size-100" marginTop="size-0">Demographics</Heading>
                        <Text>Please fill out the following demographic information. <b>All questions are mandatory unless indicated otherwise.</b></Text> 
                        <Form onSubmit={e => submitForm(e)}>

                            <Heading level={2} width="100%" marginBottom="size-100" marginTop="size-200">Basic Information</Heading>        
                            
                            {/* Age */}
                            <RadioGroup marginBottom="size-100" onChange={setAge} label={<Heading level={3} width="100%" marginBottom="size-100" marginTop="size-0">What is your age?</Heading>}>                     
                                <Radio  value='18-24'>18-24</Radio>
                                <Radio  value='25-34'>25-34</Radio>
                                <Radio  value='35-44'>35-44</Radio>
                                <Radio  value='45-54'>45-54</Radio>
                                <Radio  value='55-64'>55-64</Radio>
                                <Radio  value='65-74'>65-74</Radio>
                                <Radio  value='75+'>75 or older</Radio>
                                <Radio  value='no-disclose'>Prefer not to disclose</Radio>
                            </RadioGroup>

                            {/* Gender */}
                            <RadioGroup marginBottom="size-100" onChange={setGender}  label={<Heading level={3} width="100%" marginBottom="size-100" marginTop="size-0">What is your gender?</Heading>}>                     
                                <Radio  value='man'>Man</Radio>
                                <Radio  value='woman'>Woman</Radio>
                                <Radio  value='non-binary'>Non-binary</Radio>
                                <Radio  value='no-disclose'>Prefer not to disclose</Radio>
                                <Radio  value='other'>Other (please describe in Additional Comments below)</Radio>
                            </RadioGroup>

                            {/* Language Proficiency */}
                            <RadioGroup marginBottom="size-100" onChange={setLanguageProficiency}  label={<Heading level={3} width="100%" marginBottom="size-100" marginTop="size-0">What is your English reading proficiency?</Heading>}>                     
                                <Radio  value='elementary'>Elementary proficiency</Radio>
                                <Radio  value='limited'>Limited working proficiency</Radio>
                                <Radio  value='min-professional'>Minimum professional proficiency</Radio>
                                <Radio  value='full-professional'>Full professional proficiency</Radio>
                                <Radio  value='native-billingual'>Native or billingual proficiency</Radio>
                            </RadioGroup>

                            {/* Education */}
                            <RadioGroup marginBottom="size-100" onChange={setEducation}  label={<Heading level={3} width="100%" marginBottom="size-100" marginTop="size-0">What is the highest level of education you have completed?</Heading>}>                     
                                <Radio  value='less-hs'>Less than a high school diploma</Radio>
                                <Radio  value='hs'>High school diploma</Radio>
                                <Radio  value='some-uni'>Some university credit (no degree)</Radio>
                                <Radio  value='technical'>Technical degree or certification (e.g., plumbing)</Radio>
                                <Radio  value='bachelor'>Bachelor's degree</Radio>
                                <Radio  value='masters'>Master's degree</Radio>
                                <Radio  value='beyond-bachelor'>Professional degree beyond a bachelor's degree (e.g., MD, JD)</Radio>
                                <Radio  value='doctorate'>Doctorate degree</Radio>
                            </RadioGroup>

                            <Heading level={2} width="100%" marginBottom="size-100" marginTop="size-200">Current Behaviours with Document Readers</Heading>

                            {/* Usage */}
                            <RadioGroup marginBottom="size-100" onChange={setReaderFrequency}  label={<Heading level={3} width="100%" marginBottom="size-100" marginTop="size-0">How often do you use a digital document viewer (like Apple Preview or Adobe Acrobat)?</Heading>}>                     
                                <Radio  value='never'>Never</Radio>
                                <Radio  value='less-monthly'>Less frequently than monthly</Radio>
                                <Radio  value='monthly'>Monthly</Radio>
                                <Radio  value='weekly'>Weekly</Radio>
                                <Radio  value='daily'>Daily</Radio>
                            </RadioGroup>

                            {/* Highlighting Frequency */}
                            {readerFrequency !== 'never' && readerFrequency !== '' ?           
                                (<RadioGroup marginBottom="size-100" onChange={setHighlightFrequency}  label={<Heading level={3} width="100%" marginBottom="size-100" marginTop="size-0">How often do you highlight text in a digital document viewer?</Heading>}>                     
                                    <Radio  value='never'>Never</Radio>
                                    <Radio  value='less-monthly'>Less frequently than monthly</Radio>
                                    <Radio  value='monthly'>Monthly</Radio>
                                    <Radio  value='weekly'>Weekly</Radio>
                                    <Radio  value='daily'>Daily</Radio>
                                </RadioGroup>) : null}
                            

                            {/* Highlighting Behaviours */}
                            {highlightFrequency !== 'never' && highlightFrequency !== '' ?
                                (<CheckboxGroup marginBottom="size-100" onChange={setHighlightBehaviours}  label={<Heading level={3} width="100%" marginBottom="size-100" marginTop="size-0">Which of the following best describes your highlighting behaviours? Select all that apply.</Heading>}>
                                    <Checkbox  value='concepts'>I highlight concepts in the text that I want to remember.</Checkbox>
                                    <Checkbox  value='comments'>I highlight when I want to add my own text comments that are related (i.e., using the "comment" feature).</Checkbox>
                                    <Checkbox  value='concentrate'>I highlight to help me concentrate while reading.</Checkbox>
                                    {/* <Checkbox  value='selective'>I tend to be selective with my highlights by not highlighting too much.</Checkbox> */}
                                    {/* <Checkbox  value='lots'>I tend to highlight lots of content.</Checkbox> */}
                                    <Checkbox  value='other'>Other</Checkbox>
                                </CheckboxGroup>): null}

                            {/* Additional Comments */}
                            <TextArea marginBottom="size-100" onChange={setAdditionalComments} label={<Heading level={3} width="100%" marginBottom="size-100" marginTop="size-0">Do you have any additional comments about your demographic information, your experience with digital document viewers, or your highlighting behaviours? (optional)</Heading>} />                 
                            

                            {useTopics === false ? null : 
                                <>
                                <Heading level={2} width="100%" marginBottom="size-100" marginTop="size-200">Topics of Interest</Heading>
    
                                {/* Interest - Science */}
                                <RadioGroup marginBottom="size-100" onChange={setNaturalSciencesInterest} orientation='horizontal'  label={<Heading level={3} width="100%" marginBottom="size-100" marginTop="size-0">How interested are you in Natural Sciences (e.g., biology, chemistry)?</Heading>}>                     
                                    <Text marginEnd={"size-500"} marginTop={"size-50"}>Not at all interested</Text>
                                    {[...Array(7)].map((e, i) => <Radio  value={(i+1)}>{(i+1)}</Radio>)}
                                    <Text marginStart={"size-500"} marginTop={"size-50"}>Very interested</Text>
                                </RadioGroup>  

                                {/* Interest - Engineering */}
                                <RadioGroup marginBottom="size-100" onChange={setEngineeringInterest} orientation='horizontal'  label={<Heading level={3} width="100%" marginBottom="size-100" marginTop="size-0">How interested are you in Engineering and Technology?</Heading>}>                     
                                    <Text marginEnd={"size-500"} marginTop={"size-50"}>Not at all interested</Text>
                                    {[...Array(7)].map((e, i) => <Radio  value={(i+1)}>{(i+1)}</Radio>)}
                                    <Text marginStart={"size-500"} marginTop={"size-50"}>Very interested</Text>
                                </RadioGroup>

                                {/* Interest - News */}
                                <RadioGroup marginBottom="size-100" onChange={setNewsPoliticsInterest} orientation='horizontal'  label={<Heading level={3} width="100%" marginBottom="size-100" marginTop="size-0">How interested are you in World News and Politics?</Heading>}>                     
                                    <Text marginEnd={"size-500"} marginTop={"size-50"}>Not at all interested</Text>
                                    {[...Array(7)].map((e, i) => <Radio  value={(i+1)}>{(i+1)}</Radio>)}
                                    <Text marginStart={"size-500"} marginTop={"size-50"}>Very interested</Text>
                                </RadioGroup>   

                                {/* Interest - Literature */}
                                <RadioGroup marginBottom="size-100" onChange={setLiteratureInterest} orientation='horizontal'  label={<Heading level={3} width="100%" marginBottom="size-100" marginTop="size-0">How interested are you in English Literature?</Heading>}>                     
                                    <Text marginEnd={"size-500"} marginTop={"size-50"}>Not at all interested</Text>
                                    {[...Array(7)].map((e, i) => <Radio  value={(i+1)}>{(i+1)}</Radio>)}
                                    <Text marginStart={"size-500"} marginTop={"size-50"}>Very interested</Text>
                                </RadioGroup>

                                {/* Interest - History */}
                                <RadioGroup marginBottom="size-100" onChange={setHistoryInterest} orientation='horizontal'  label={<Heading level={3} width="100%" marginBottom="size-100" marginTop="size-0">How interested are you in Modern European History?</Heading>}>                     
                                    <Text marginEnd={"size-500"} marginTop={"size-50"}>Not at all interested</Text>
                                    {[...Array(7)].map((e, i) => <Radio  value={(i+1)}>{(i+1)}</Radio>)}
                                    <Text marginStart={"size-500"} marginTop={"size-50"}>Very interested</Text>
                                </RadioGroup>                         

                                </>
                            }
                            <Button variant='primary' type="submit" style='fill' marginTop="size-600" isDisabled={!formValidated}>Submit</Button>
                        </Form>
                    </div>
                </Flex>
            </Provider>
        </>
    );
}

export default DemographicsForm;